import { useEffect, useState } from 'react'

/**
 * Offering to install the app from inside it.
 *
 * Chromium browsers (Chrome, Edge, Samsung Internet) fire `beforeinstallprompt`
 * once the manifest checks out, and hand over a prompt the page can show when
 * it likes. Safari on iOS has no such event — installing is "Add to Home
 * Screen" in the share sheet, so the most the page can do there is say where
 * to find it. Firefox and desktop Safari get nothing, since there's nothing to
 * point them at.
 *
 * The event can fire before React has mounted, so the listener is attached as
 * soon as this module loads (main.tsx imports it first) and the event is kept
 * until someone asks for it.
 */

/** Not in the DOM typings yet — Chromium only. */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export type InstallMethod =
  /** The browser has a prompt ready to show. */
  | 'prompt'
  /** iPhone or iPad: only the share sheet can do it. */
  | 'ios'

let deferred: BeforeInstallPromptEvent | null = null
let installed = false
const listeners = new Set<() => void>()
const notify = () => listeners.forEach((l) => l())

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Stops Chrome's own mini-infobar on Android; the banner stands in for it.
    e.preventDefault()
    deferred = e as BeforeInstallPromptEvent
    notify()
  })
  window.addEventListener('appinstalled', () => {
    deferred = null
    installed = true
    notify()
  })
}

/** Already running from the home screen or as an installed window. */
function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

/**
 * Whether this visit began from the installed app rather than a web page. The
 * manifest's start_url carries `?source=pwa`, read once at load because the
 * visit is still the app's after the address bar changes. Display mode is the
 * second signal, for installs that predate the parameter.
 */
const openedAsApp =
  typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('source') === 'pwa'

export function launchedAsApp(): boolean {
  return openedAsApp || isStandalone()
}

function isIOS(): boolean {
  // iPadOS reports itself as a Mac, so a touchscreen is what gives it away.
  return (
    /iPhone|iPad|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )
}

function currentMethod(): InstallMethod | null {
  if (installed || isStandalone()) return null
  if (deferred) return 'prompt'
  if (isIOS()) return 'ios'
  return null
}

/** How this browser could install the app right now, or null if it can't (or already has). */
export function useInstallMethod(): InstallMethod | null {
  const [method, setMethod] = useState(currentMethod)
  useEffect(() => {
    const update = () => setMethod(currentMethod())
    listeners.add(update)
    update()
    return () => {
      listeners.delete(update)
    }
  }, [])
  return method
}

/**
 * Shows the browser's install prompt. A prompt can only be used once, so it's
 * dropped either way; Chrome fires a fresh event later if the person said no.
 */
export async function promptInstall(): Promise<void> {
  const e = deferred
  if (!e) return
  deferred = null
  notify()
  await e.prompt()
  await e.userChoice
}

// Kept apart from the plan caches, so signing out doesn't bring the banner back.
const DISMISSED_KEY = 'snapshot-money:install-dismissed:v1'

export function hasInstallBeenDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1'
  } catch {
    return false
  }
}

export function dismissInstall(): void {
  try {
    localStorage.setItem(DISMISSED_KEY, '1')
  } catch {
    // Nowhere to remember it — the banner shows again next visit.
  }
}
